import React, { useState, useEffect, useRef } from 'react';
import api, { handleResponse } from '../services/api';
import clubService from '../services/clubService';
import eventService from '../services/eventService';
import { useDialog } from '../context';
import { SubPanel, Ava, Tag, Help, Note } from './host/kit';

/**
 * LinkPagesPanel — PUT IT ON A PAGE, the last block of THE EXTRAS in the event
 * builder (docs/plans/2026-09-pro-multipage-events/PLAN.md, F3), drawn on the
 * host kit (docs/plans/2026-09-host-event-edit, Step 4 EXTRAS · PUT IT ON A PAGE).
 *
 * One sub-panel grouped as YOUR CLUBS and YOUR PRO PAGES. Every row is a whole-row
 * switch (`role="switch"`, named `List on {name}`) that links / unlinks the draft
 * immediately (the CoHostsPanel precedent — a side effect against the
 * already-created draft, so it never touches useAutoSave).
 *
 *   clubs      clubService.linkEventToClub / unlinkEventFromClub — as many clubs as the
 *              person belongs to (`club_events`), each row its own switch.
 *   pro pages  eventService.linkPageEvent / unlinkPageEvent (many pages per event).
 *
 * Renders nothing when the person has neither a club nor a Pro page. Without a
 * minted draft (`eventId` null — autosave creates it on the first title keystroke)
 * the switches are disabled and a note says why.
 *
 * `clubIdFromQuery` (?clubId= / the gathering wizard) is linked to the draft as
 * soon as the draft exists and the club is one of the person's own; `onChange`
 * reports `queryClubHandled: true` so the builder skips its publish-time link.
 *
 * `onChange({ clubIds, clubNames, proPageIds, proPageNames, queryClubHandled })`
 * fires whenever the linked set changes — the Review step's LISTED ON row.
 */
export default function LinkPagesPanel({ eventId, event, clubIdFromQuery = null, onChange = null }) {
  const { showAlert } = useDialog();
  const [clubs, setClubs] = useState(null);   // null = loading; [] = none / endpoint missing
  const [pages, setPages] = useState(null);
  const [clubIds, setClubIds] = useState([]);
  const [pageIds, setPageIds] = useState([]);
  const [busy, setBusy] = useState(null);     // id of the row mid-request
  const [queryClubHandled, setQueryClubHandled] = useState(false);
  const seededFor = useRef(null);
  const queryLinkedFor = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // ── Load the person's clubs + Pro pages once ────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [c, p] = await Promise.all([loadMyClubs(), loadMyProPages()]);
      if (cancelled) return;
      setClubs(c);
      setPages(p);
    })();
    return () => { cancelled = true; };
  }, []);

  // ── Seed the linked set from the loaded event (edit / inline) or blank (new draft) ──
  useEffect(() => {
    if (!eventId) {
      seededFor.current = null;
      setClubIds([]);
      setPageIds([]);
      return;
    }
    const fromEvent = event && String(event.id) === String(eventId) ? event : null;
    const stamp = `${eventId}|${fromEvent ? 'event' : 'blank'}`;
    if (seededFor.current === stamp) return;
    seededFor.current = stamp;
    // `clubIds` is every club the event is listed on; older payloads only carry `clubId`.
    const seededClubs = Array.isArray(fromEvent?.clubIds) && fromEvent.clubIds.length
      ? fromEvent.clubIds.map(String)
      : fromEvent?.clubId ? [String(fromEvent.clubId)] : [];
    setClubIds(seededClubs);
    setPageIds(Array.isArray(fromEvent?.proPageIds) ? fromEvent.proPageIds.map(String) : []);
  }, [eventId, event]);

  // ── ?clubId= — link the draft to that club as soon as the draft exists ──────
  useEffect(() => {
    if (!eventId || !clubIdFromQuery || clubs === null) return;
    if (queryLinkedFor.current === eventId) return;
    const target = String(clubIdFromQuery);
    if (!clubs.some((c) => String(c.id) === target)) return; // not one of theirs — the publish path still tries
    queryLinkedFor.current = eventId;
    let cancelled = false;
    (async () => {
      const r = await clubService.linkEventToClub(target, eventId);
      if (cancelled) return;
      if (r.success) {
        setClubIds((prev) => Array.from(new Set([...prev, target])));
        setQueryClubHandled(true);
      } else {
        queryLinkedFor.current = null; // let the publish-time fallback retry
      }
    })();
    return () => { cancelled = true; };
  }, [eventId, clubIdFromQuery, clubs]);

  // ── Tell the builder what the event is listed on (Review → LISTED ON) ───────
  useEffect(() => {
    if (clubs === null || pages === null) return;
    const linkedClubs = clubs.filter((c) => clubIds.includes(String(c.id)));
    const linkedPages = pages.filter((p) => pageIds.includes(String(p.id)));
    onChangeRef.current?.({
      clubIds,
      clubNames: linkedClubs.map((c) => c.name),
      proPageIds: pageIds,
      proPageNames: linkedPages.map((p) => p.name),
      queryClubHandled,
    });
  }, [clubs, pages, clubIds, pageIds, queryClubHandled]);

  const fail = async (title, message) => {
    await showAlert({ title, message: message || 'Something went wrong. Try again.', type: 'error' });
  };

  const toggleClub = async (club, on) => {
    if (!eventId || busy) return;
    const id = String(club.id);
    setBusy(id);
    setClubIds((prev) => (on ? Array.from(new Set([...prev, id])) : prev.filter((x) => x !== id)));
    const r = on ? await clubService.linkEventToClub(id, eventId) : await clubService.unlinkEventFromClub(id, eventId);
    if (!r.success) {
      setClubIds((prev) => (on ? prev.filter((x) => x !== id) : Array.from(new Set([...prev, id]))));
      await fail(on ? 'Couldn’t list it there' : 'Couldn’t take it off', r.error);
    }
    setBusy(null);
  };

  const togglePage = async (page, on) => {
    if (!eventId || busy) return;
    const id = String(page.id);
    setBusy(id);
    setPageIds((prev) => (on ? Array.from(new Set([...prev, id])) : prev.filter((x) => x !== id)));
    const r = on ? await eventService.linkPageEvent(id, eventId) : await eventService.unlinkPageEvent(id, eventId);
    if (!r.success) {
      setPageIds((prev) => (on ? prev.filter((x) => x !== id) : Array.from(new Set([...prev, id]))));
      await fail(on ? 'Couldn’t list it there' : 'Couldn’t take it off', r.error);
    }
    setBusy(null);
  };

  if (clubs === null || pages === null) return null;
  if (clubs.length === 0 && pages.length === 0) return null;

  const locked = !eventId;

  return (
    <SubPanel title="PUT IT ON A PAGE" sub="Your clubs and Pro pages can list this event." testId="link-pages-panel">
      {locked && (
        <Note tag="LOCKED" tone="warning" small testId="link-pages-locked">
          Add a title first — the draft is created as you type.
        </Note>
      )}

      {clubs.length > 0 && (
        <PageGroup label="Your clubs" count={clubIds.length} testId="link-pages-clubs">
          {clubs.map((club) => {
            const id = String(club.id);
            return (
              <PageRow
                key={id}
                id={id}
                name={club.name}
                imageUrl={club.avatarUrl}
                meta={club.memberCount != null ? `${club.memberCount} ${club.memberCount === 1 ? 'member' : 'members'}` : null}
                role={roleLabel(club.userRole || club.role)}
                on={clubIds.includes(id)}
                disabled={locked || !!busy}
                onToggle={(next) => toggleClub(club, next)}
                testId={`link-club-${id}`}
              />
            );
          })}
          {clubs.length > 1 && (
            <Help>List it on every club you belong to — each club shows it on its EVENTS tab.</Help>
          )}
        </PageGroup>
      )}

      {pages.length > 0 && (
        <PageGroup label="Your Pro pages" count={pageIds.length} testId="link-pages-pro">
          {pages.map((page) => {
            const id = String(page.id);
            return (
              <PageRow
                key={id}
                id={id}
                name={page.name}
                imageUrl={page.logoUrl}
                meta={[page.kind, page.isPublished === false ? 'not published' : null].filter(Boolean).join(' · ') || null}
                role={roleLabel(page.role)}
                on={pageIds.includes(id)}
                disabled={locked || !!busy}
                onToggle={(next) => togglePage(page, next)}
                testId={`link-page-${id}`}
              />
            );
          })}
        </PageGroup>
      )}
    </SubPanel>
  );
}

/** YOUR CLUBS / YOUR PRO PAGES: a 9px header with `LISTED ON n` / `NOT LISTED` on the right. */
function PageGroup({ label, count, children, testId }) {
  return (
    <div data-testid={testId} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, fontSize: 9, fontWeight: 700, letterSpacing: '.16em', textTransform: 'uppercase' }}>
        <span>{label}</span>
        <span style={{ color: '#6B7280', whiteSpace: 'nowrap' }}>{count ? `Listed on ${count}` : 'Not listed'}</span>
      </span>
      {children}
    </div>
  );
}

/**
 * One white row pill, the whole row a switch: tinted initial (or the image), name, meta,
 * the role tag, and the state pill — `+ LIST` in black or `LISTED ✓` on cyan.
 */
function PageRow({ id, name, imageUrl, meta, role, on, disabled, onToggle, testId }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={`List on ${name}`}
      disabled={disabled}
      onClick={() => onToggle(!on)}
      data-testid={testId}
      style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '10px 14px 10px 10px', border: 'none', borderRadius: 999, background: '#fff', color: '#000', fontFamily: 'inherit', textAlign: 'left', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1, transition: 'opacity .2s' }}
    >
      <Ava size={34} name={name} imageUrl={imageUrl} tintKey={id} />
      <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.04em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>
        {meta && (
          <span style={{ fontSize: 9, letterSpacing: '.12em', color: '#6B7280', textTransform: 'uppercase', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{meta}</span>
        )}
      </span>
      <Tag small>{role}</Tag>
      <span style={{ flexShrink: 0, height: 30, padding: '0 12px', borderRadius: 999, background: on ? '#00FFFF' : '#000', color: on ? '#000' : '#fff', display: 'flex', alignItems: 'center', fontSize: 9, fontWeight: 700, letterSpacing: '.14em', whiteSpace: 'nowrap', transition: 'background-color .2s, color .2s' }} aria-hidden="true">
        {on ? 'LISTED ✓' : '+ LIST'}
      </span>
    </button>
  );
}

function roleLabel(role) {
  const r = String(role || 'member').toLowerCase();
  return r === 'owner' ? 'OWNER' : r === 'admin' ? 'ADMIN' : 'MEMBER';
}

/** GET /clubs/my → [{ id, name, avatarUrl, memberCount, userRole }]. Any failure → none (the half hides). */
async function loadMyClubs() {
  const r = await clubService.getMyClubs();
  return r?.success && Array.isArray(r.data) ? r.data : [];
}

/**
 * GET /pro/mine → [{ id, slug, name, kind, logoUrl, isPublished, isListed, role, createdAt }]
 * (PLAN.md contract). Called through `api` directly so this file doesn't collide with
 * F1's additions to proService; a 404 while the endpoint lands means "no pages".
 */
async function loadMyProPages() {
  try {
    const r = handleResponse(await api.get('/pro/mine'));
    return r?.success && Array.isArray(r.data) ? r.data : [];
  } catch (e) {
    return [];
  }
}
